import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "./shared";

const Logo = ({ scrolled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="nav-logo"
    className="flex items-center"
    aria-label="Oswal Coslabs Private Limited — back to top"
  >
    <img
      src="/assets/oswal-logo.png"
      alt="OSWAL COSLABS PRIVATE LIMITED — official logo"
      className={`h-auto transition-all duration-500 ${
        scrolled
          ? "w-[140px] sm:w-[160px] lg:w-[172px]"
          : "w-[150px] sm:w-[175px] lg:w-[205px]"
      }`}
    />
  </button>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [open]);

  const go = (id) => {
    setOpen(false);
    window.setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-softblue bg-white/90 shadow-[0_10px_36px_-20px_rgba(11,94,168,0.4)] backdrop-blur-md"
            : "border-b border-transparent bg-white/0"
        }`}
      >
        <div
          className={`mx-auto flex max-w-[1600px] items-center justify-between px-6 transition-all duration-500 md:px-12 ${
            scrolled ? "py-3" : "py-5"
          }`}
        >
          <Logo scrolled={scrolled} onClick={() => go("home")} />

          <nav
            className="hidden items-center gap-7 lg:flex xl:gap-9"
            aria-label="Primary"
          >
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => go(link.id)}
                data-testid={`nav-link-${link.id}`}
                className={`link-underline text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                  active === link.id ? "is-active text-brand" : "text-fog hover:text-navy"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            className="text-navy lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed inset-0 z-[80] flex flex-col bg-navy text-white"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo onClick={() => go("home")} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="text-white/80 transition-colors hover:text-white"
              >
                <X size={28} strokeWidth={1.25} />
              </button>
            </div>

            <nav
              className="flex flex-1 flex-col justify-center gap-2 overflow-y-auto px-8"
              aria-label="Mobile"
            >
              {NAV_LINKS.map((link, i) => (
                <div key={link.id} className="overflow-hidden">
                  <motion.button
                    type="button"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.06, ease: EASE }}
                    onClick={() => go(link.id)}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className="flex w-full items-baseline gap-4 py-1.5 text-left"
                  >
                    <span className="text-[11px] font-semibold tracking-[0.3em] text-brand-bright">
                      0{i + 1}
                    </span>
                    <span className="font-serif text-[42px] font-medium leading-tight tracking-tight text-white">
                      {link.label}
                    </span>
                  </motion.button>
                </div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="flex items-center justify-between border-t border-white/10 px-8 py-6"
            >
              <a
                href={`mailto:${CONTACT.email}`}
                className="link-underline text-xs tracking-[0.18em] text-white/70"
                data-testid="mobile-menu-email"
              >
                {CONTACT.email}
              </a>
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                Ahmedabad, India
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
